import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AvaModule, Field, Module } from '../../Models';
import { combineLatest, Subject } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FeedService } from '../../Services/feed.service';
import { FeedTypeDescription } from '@app/Constants/FeedTypeDescriptions';
import { FeedTypes } from '@app/Constants/FeedTypes';
import { IFeedConfiguration } from '../../Services/IFeedConfiguration';
import { AttributeMapping, IFeedConfigurationDataField } from '@app/Tenant/Applications/Airsweb/Services';
import { ILocationLevel } from '../../Services/ILocationLevel';
import { ImportService, StateItem } from '../../Services/import.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ModuleRecordFeedMappableEntityDto } from '../../Models/FeedConfigurationModels/ModuleRecordFeedMappableEntityDto';
import { ModuleService } from '../../Services/module.service';
import { NgForm, FormsModule } from '@angular/forms';
import { Tenant } from '@app/Models/tenant';
import { TenantService } from '@app/Services/tenant.service';
import { ValidationRegexPatterns } from '@app/Constants/ValidationRegexPatterns';
import { LocationAttributesService } from '@app/Tenant/Applications/Airsweb/Services';
import { takeUntil } from 'rxjs/operators';
import { LocationAttribute } from '../../Models/location-attribute.model';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatCardActions, MatCardFooter } from '@angular/material/card';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatFormField, MatInput, MatHint, MatLabel } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { TranslationPipe } from '@app/Services/translation.pipe';
import { FeedAutoMapperService } from '../feed-auto-mapper/feed-auto-mapper.service';

export interface ISelectItem {
    FriendlyName: string;
    Key: string;
}

@Component({
    selector: 'app-feed-configuration-detail',
    templateUrl: './feed-configuration-detail.component.html',
    styleUrls: ['./feed-configuration-detail.component.scss'],
    imports: [MatCard, MatProgressBar, FormsModule, MatCardHeader, MatCardTitle, MatCardContent, FlexModule, MatFormField, MatInput, MatHint, MatSlideToggle, MatLabel, MatSelect, MatOption, MatButton, MatIcon, NgClass, ExtendedModule, MatCheckbox, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatCardActions, MatCardFooter, RouterLink, AsyncPipe, TranslationPipe]
})
export class FeedConfigurationDetailComponent implements OnInit, OnDestroy {
    configId: string;
    saveFeedConfigurationErrorText: string = null;
    fetchFileErrorText: string = null;
    feedNameErrorText: string = null;
    filePathErrorText: string = null;
    identifierErrorText: string = null;
    fileNameRegexErrorText: string = null;
    moduleIsEnabled = true;
    multipleEmailValidation: string = ValidationRegexPatterns.MultipleEmailPattern;
    lists: ISelectItem[] = [];
    locationLevels: ILocationLevel[] = [];
    dataTypes: ISelectItem[] = [];
    recordFeedModules: Module[];
    states: StateItem[];
    formFlowFields: Field[];
    mappableEntities: ModuleRecordFeedMappableEntityDto[];
    linkedRecordOptions: Map<string, string[]> = new Map<string, string[]>();

    hierarchyTypes: ISelectItem[] = [
        {
            FriendlyName: 'ShortName',
            Key: 'all_nodes_shortnames',
        },
        {
            FriendlyName: 'Name',
            Key: 'all_nodes',
        },
        {
            FriendlyName: 'Address',
            Key: 'all_nodes_addresses',
        },
    ];

    oshaEstablishmentTypes: ISelectItem[] = [];
    formatStrings: ISelectItem[] = [];
    moduleVersions: Map<string, number> = new Map();
    model: IFeedConfiguration;
    feedTypes: ISelectItem[] = [];
    isLoading = true;
    showSyncColumn = false;
    hierarchyAttributes: LocationAttribute[] = [];
    reportingPoint = 'REPORTING_POINT';
    selectedAttributes: LocationAttribute[] = [];
    overwritePreviousMappings = false;

    private isFilePathPatternUnique: boolean;
    private isFeedNameUnique: boolean;
    private tenant: Tenant = this.tenantService.selectedTenant;
    private submitting = false;
    private emptyGuid = '00000000-0000-0000-0000-000000000000';
    private msgConfigSaved = 'Configuration Saved';
    private msgUniqueName = 'Feed name must be unique';
    private msgUniqueFilePath = 'Folder Location must be unique';
    private msgErrorFileNameRegex =
        'File Name Regex must be a valid regular expression';
    private msgRequiredLocation = 'Folder Location must be entered';
    private msgRequiredPattern = 'File Pattern must be entered';
    private msgSingleIdentifier = "Please select only one field as 'Identifier'";
    private msgErrorSave = 'Error occurred whilst attempting to save data';
    private msgErrorNoFile = 'No file found in the specified folder';
    private msgErrorServer = 'Error occurred whilst attempting to fetch file';
    private msgErrorOther = 'Error occurred whilst attempting to fetch file';

    private isDestroyed$: Subject<void> = new Subject();

    public constructor(
        private router: Router,
        private route: ActivatedRoute,
        private importService: ImportService,
        private tenantService: TenantService,
        private moduleService: ModuleService,
        private feedService: FeedService,
        private snackBar: MatSnackBar,
        private locationAttributeService: LocationAttributesService,
        private feedAutoMapperService: FeedAutoMapperService
    ) { }

    ngOnInit(): void {
        this.lists = [{ FriendlyName: null, Key: null }];
        this.subscribeToLists();
        this.subscribeToFeedConfiguration();
        this.setLocationLevels();
        this.buildDateFormatStrings();
    }

    initialiseModuleRecordFeed(): void {
        combineLatest([
            this.tenantService.getAllTenantModules(this.tenant.Value),
            this.tenantService.getRecordFeedModules(this.tenant),
            this.tenantService.getAllowedModules(this.tenant.Value),
        ])
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(([availableModules, importableModules, enabledModules]) => {
                const availModules = availableModules.map((x: AvaModule) =>
                    Module.fromAvaModule(x)
                );
                this.recordFeedModules = availModules.filter(
                    (x: Module) =>
                        importableModules.some((moduleId) => moduleId === x.ModuleId) &&
                        enabledModules.some((moduleId) => moduleId === x.ModuleId)
                );

                this.setIsModuleEnabled();

                if (this.moduleIsEnabled) {
                    this.moduleChanged();

                    if (this.model.StateId) {
                        this.stateChanged();
                    }
                }
            });
    }

    feedSelectionChanged(): void {
        if (
            this.model.FeedType === FeedTypes[FeedTypes.ModuleRecordFeed] &&
            this.model.Name === null
        ) {
            this.model.EmailOnSuccess = true;
        } else if (this.model.Name === null) {
            this.model.EmailOnSuccess = false;
        }
        this.getListOfDataTypes();
        this.setOshaEstablishmentTypes();
    }

    showEmailInput(): boolean {
        return this.model.EmailOnSuccess;
    }

    isHierarchyFeed(): boolean {
        return this.model.FeedType === FeedTypes[FeedTypes.HierarchyFeed];
    }

    isHierarchyOshaAttributesFeed(): boolean {
        return (
            this.model.FeedType ===
            FeedTypes[FeedTypes.HierarchyOSHAEstablishmentFeed]
        );
    }

    isHierarchyAttributesDataType(index: number): boolean {
        return this.model.DataFields[index].DataType === 'attributes';
    }

    isListWithAttributes(index: number): boolean {
        const field = this.model.DataFields[index];
        return this.isListOrAttributeFilter(field) && (field.AttributeMappings ?? []).length > 0;
    }

    isPermissionFeed(): boolean {
        return this.model.FeedType === FeedTypes[FeedTypes.PermissionFeed];
    }

    isOshaEstablishmentFeed(): boolean {
        return this.model.FeedType === FeedTypes[FeedTypes.OshaEstablishmentFeed];
    }

    isModuleRecordFeed(): boolean {
        return this.model.FeedType === FeedTypes[FeedTypes.ModuleRecordFeed];
    }

    isValidNaturalKey(index: number): boolean {
        // only valid if not a linked field to the same module (causing issues with merge on existing records)
        return (
            !this.model.DataFields[index].LinkedRecordModuleId ||
            this.model.DataFields[index].LinkedRecordModuleId !== this.model.ModuleId
        );
    }

    isAttributeValidNaturalKey(att: AttributeMapping): boolean {
        return (
            !att.LinkedRecordModuleId ||
            att.LinkedRecordModuleId !== this.model.ModuleId
        );
    }

    fileNameRegexIsValidRegex(): boolean {
        let isValid = true;
        try {
            new RegExp(this.model.FileNameRegex);
        } catch (exception) {
            this.fileNameRegexErrorText = this.msgErrorFileNameRegex;
            isValid = false;
        }
        return isValid;
    }

    showDataTypeColumn(): boolean {
        return (
            !this.isHierarchyOshaAttributesFeed() &&
            !this.isOshaEstablishmentFeed() &&
            !this.isModuleRecordFeed()
        );
    }

    showSelectionColumn(): boolean {
        return !this.isModuleRecordFeed();
    }

    showMapsToColumn(): boolean {
        return this.isModuleRecordFeed();
    }

    showSyncListColumn(): boolean {
        return (
            this.showSyncColumn &&
            !this.isHierarchyFeed() &&
            !this.isHierarchyOshaAttributesFeed() &&
            !this.isOshaEstablishmentFeed() &&
            !this.isModuleRecordFeed()
        );
    }

    showIdentifierColumn(): boolean {
        return (
            !this.isHierarchyFeed() &&
            !this.isHierarchyOshaAttributesFeed() &&
            !this.isOshaEstablishmentFeed() &&
            !this.isModuleRecordFeed()
        );
    }

    showSearchKeyColumn(): boolean {
        return (
            !this.isHierarchyFeed() &&
            !this.isHierarchyOshaAttributesFeed() &&
            !this.isOshaEstablishmentFeed() &&
            !this.isModuleRecordFeed()
        );
    }

    showNaturalKeyColumn(): boolean {
        return this.isModuleRecordFeed();
    }

    showEnabledColumn(): boolean {
        return this.isModuleRecordFeed();
    }

    showLevelColumn(): boolean {
        return this.isHierarchyFeed();
    }

    calculateSyncColumn(): void {
        let result = false;

        if (
            this.model &&
            this.model.DataFields.length > 0 &&
            !this.isHierarchyFeed() &&
            !this.isOshaEstablishmentFeed()
        ) {
            result = this.model.DataFields.some(
                (f) => f.DataType === this.dataTypes[1].Key
            );
        }
        this.calculateSyncDisabled();
        this.showSyncColumn = result;
    }

    calculateSyncDisabled(): void {
        this.model.DataFields.forEach((outerField, outerIndex) => {
            let result = false;
            if (this.model.AlreadySyncedLists.indexOf(outerField.ListKey) > -1) {
                result = true;
            } else {
                if (outerField.ListKey) {
                    this.model.DataFields.forEach((innerField, innerIndex) => {
                        if (
                            this.dataTypes[1] !== undefined &&
                            innerField.DataType === this.dataTypes[1].Key &&
                            innerField.ListKey === outerField.ListKey &&
                            innerField.SyncList === true &&
                            innerIndex !== outerIndex
                        ) {
                            result = true;
                        }
                    });
                } else {
                    result = true;
                }
            }
            outerField.DisableSync = result;
        });
    }

    getRouteConfigId(): string {
        const newExists = this.route.snapshot.url.some(
            (url) => url.path.indexOf('new') !== -1
        );
        if (newExists) {
            return this.emptyGuid;
        }
        return this.route.snapshot.params.id;
    }
  
  getAssociatedAttributeMappings(fieldIdx: number): AttributeMapping[] {
    if (this.isModuleRecordFeed()) {
      return this.model.DataFields[fieldIdx].AttributeMappings;
    }
    return  [];
  }

    onSubmit(feedConfigurationForm: NgForm): void {
        if (!this.formCanSubmit(feedConfigurationForm)) {
            return;
        }

        this.submitting = true;
        this.saveFeedConfigurationErrorText = null;
        this.model.SSOFeed = this.isPermissionFeed();

        if (this.isModuleRecordFeed()) {
            this.model.FormFlowId = this.model.ModuleId;
        }

        this.feedService
            .saveFeedConfiguration(
                this.tenantService.selectedTenant.Value,
                this.model
            )
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(
                (result) => {
                    this.model.Id = result;
                    feedConfigurationForm.form.markAsPristine();
                    this.submitting = false;
                    this.showSaveConfirmationMessage();
                },
                (_) => {
                    this.handleSaveFeedConfigurationError();
                    this.submitting = false;
                }
            );

    }

    formCanSubmit(feedConfigurationForm: NgForm): boolean {
        return (
            feedConfigurationForm.form.valid &&
            feedConfigurationForm.form.dirty &&
            this.isDataFieldsValid() &&
            this.isHierarchyFeedValid() &&
            this.isHierarchyDataFieldsValid() &&
            this.isHierarchyOshaAttributesFeedValid() &&
            this.isOshaEstablishmentFeedValid() &&
            this.isSSOFeedValid() &&
            this.isModuleRecordFeedValid() &&
            this.fileNameRegexIsValidRegex() &&
            this.isFeedNameUnique &&
            this.isFilePathPatternUnique &&
            !this.submitting
        );
    }

    isSSOFeedValid(): boolean {
        if (!this.model.SSOFeed) {
            return true;
        }
        return (
            this.model.SSOFeed &&
            this.getIdentifierRowsCount() === 1 &&
            this.model.DataFields.some((f) => f.DataType === 'hierarchy') &&
            this.model.SSOAvaIdentifier !== null
        );
    }

    checkFeedNameUnique(): void {
        this.feedService
            .getFeedConfigurationsFromName(
                this.tenantService.selectedTenant.Value,
                this.model.Name
            )
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((configs) => {
                const unique =
                    configs.filter((x) => x && x.Id !== this.model.Id).length === 0;
                this.isFeedNameUnique = unique;
                this.feedNameErrorText = unique ? '' : this.msgUniqueName;
            });
    }

    checkFolderFilePatternUnique(): void {
        this.feedService
            .getFeedConfigurationsFromPath(
                this.tenantService.selectedTenant.Value,
                this.model.SftpLocation,
                this.model.FilePattern
            )
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((configs) => {
                const unique =
                    configs.filter((x) => x.Id !== this.model.Id).length === 0;
                this.isFilePathPatternUnique = unique;
                this.filePathErrorText = unique ? '' : this.msgUniqueFilePath;
            });
    }

    checkIdentifierCount(): void {
        this.identifierErrorText =
            this.model.SSOFeed && this.getIdentifierRowsCount() > 1
                ? this.msgSingleIdentifier
                : '';
    }

    isDataFieldsValid(): boolean {
        if (
            this.isHierarchyFeed() ||
            this.isOshaEstablishmentFeed() ||
            this.isHierarchyOshaAttributesFeed() ||
            this.isModuleRecordFeed()
        ) {
            return true;
        }
        return this.model.DataFields.some((f) => f.IsSearchKey);
    }

    isHierarchyDataFieldsValid(): boolean {
        if (!this.isHierarchyFeed()) {
            return true;
        }
        return !this.model.DataFields.some((f) => f.LocationLevel === null);
    }

    isHierarchyFeedValid(): boolean {
        let hierarchyFeedValid = true;
        if (this.isHierarchyFeed()) {
            const data = this.model.DataFields;
            this.model.DataFields.forEach((dataField) => {
                if (
                    dataField !== null &&
                    hierarchyFeedValid &&
                    dataField.ListKey !== null
                ) {
                    if (
                        data.filter(
                            (datum) =>
                                datum.LocationLevel != null &&
                                datum.ListKey != null &&
                                dataField.LocationLevel != null &&
                                datum.AttributeId != null &&
                                dataField.AttributeId != null &&
                                datum.LocationLevel.toString() ===
                                dataField.LocationLevel.toString() &&
                                datum.ListKey.toString() === dataField.ListKey.toString() &&
                                datum.AttributeId.toString() === dataField.AttributeId.toString()
                        ).length > 1
                    ) {
                        hierarchyFeedValid = false;
                    }
                }
            });
            if (hierarchyFeedValid) {
                hierarchyFeedValid = this.isReportingPointLevelMapped();
            }
        }
        return hierarchyFeedValid;
    }

    isReportingPointLevelMapped(): boolean {
        const data = this.model.DataFields;
        let reportingPointLevelMapped = true;

        for (let type of this.hierarchyTypes) {
            const listKey = type.Key;

            if (listKey === 'all_nodes_addresses') {
                continue;
            }

            reportingPointLevelMapped =
                reportingPointLevelMapped &&
                data.filter(
                    (datum) =>
                        datum.LocationLevel !== null &&
                        datum.LocationLevel.toString() === 'REPORTING_POINT' &&
                        datum.ListKey === listKey
                ).length === 1;
        }

        return reportingPointLevelMapped;
    }

    getMandatoryFormflowFields(): ModuleRecordFeedMappableEntityDto[] {
        if (!this.mappableEntities) {
            return [];
        }

        return this.mappableEntities.filter((field) => field.IsMandatoryForFeed);
    }

    areMandatoryFieldsMapped(): boolean {
        if (!this.mappableEntities) {
            return false;
        }

        const mandatoryFormFlowFields = this.getMandatoryFormflowFields()
            .map((x) => x.FieldId)
            .filter(
                (value: string, index: number, self: string[]) =>
                    self.indexOf(value) === index
            );
        const mappedMandatoryDataFields = this.model.DataFields.filter((x) =>
            mandatoryFormFlowFields.includes(x.RecordPropertyName)
        );

        return mandatoryFormFlowFields.length <= mappedMandatoryDataFields.length;
    }

    hasNaturalKey(): boolean {
        return this.model.DataFields?.some(
            (field) => field.Enabled && !!field.RecordPropertyName?.trim() && field.IsNaturalKey
        );
    }

    isModuleRecordFeedValid(): boolean {
        let isValid = true;

        if (this.isModuleRecordFeed()) {
            if (!this.model.ModuleId || this.model.StateId <= 0) {
                isValid = false;
            } else if (
                !this.model.DataFields.some(
                    (field) => field.RecordPropertyName !== null
                )
            ) {
                isValid = false;
            } else {
                isValid = this.areMandatoryFieldsMapped() && this.hasNaturalKey();
            }
        }

        return isValid;
    }

    isHierarchyOshaAttributesFeedValid(): boolean {
        if (!this.isHierarchyOshaAttributesFeed()) {
            return true;
        }
        return (
            this.model.DataFields.filter(
                (x) =>
                    x.ListKey === 'EstablishmentName' ||
                    x.ListKey === 'ReportingPointShortName'
            ).length === 2
        );
    }

    isOshaEstablishmentFeedValid(): boolean {
        if (!this.isOshaEstablishmentFeed()) {
            return true;
        }
        return (
            this.model.DataFields.filter((x) => x.ListKey === 'EstablishmentName')
                .length === 1
        );
    }

    onCancelButtonClick(): void {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    isDataFieldMappedToMandatoryField(
        dataField: IFeedConfigurationDataField
    ): boolean {
        return (
            this.formFlowFields &&
            this.formFlowFields.some(
                (field) =>
                    field.IsMandatoryForFeed &&
                    field.FieldId === dataField.RecordPropertyName
            )
        );
    }

    fetchFileButtonClick(folderLocation: string, filePattern: string): void {
        if (!folderLocation) {
            this.fetchFileErrorText = this.msgRequiredLocation;
            return;
        }
        if (!filePattern) {
            this.fetchFileErrorText = this.msgRequiredPattern;
            return;
        }

        this.fetchFileErrorText = null;
        this.importService
            .getLatestFileContentsFromFolder(this.tenant.Value, folderLocation, filePattern)
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(
                (fileContents) => this.populateFieldListFromFileContents(fileContents),
                (error) => this.handleFetchFileError(error)
            );
    }

    mappableEntityKey(entity: ModuleRecordFeedMappableEntityDto): string {
        return this.buildMapsToKey(entity.FieldId, entity.ModelPropertyName, entity.ListKey, entity.Path);
    }

    mapsToKey(mapping: IFeedConfigurationDataField | AttributeMapping): string {
        return this.buildMapsToKey(mapping.RecordPropertyName, mapping.ModelPropertyName, mapping.ListKey, mapping.Path);
    }

    mapsToChanged(selection: string, dataFieldIndex: number): void {
        if (!selection) {
            return;
        }

    const mappableEntity = this.findMappableEntityByKey(selection);
    if (!mappableEntity) {
      return;
    }

    const dataField = this.model.DataFields[dataFieldIndex];
    dataField.DataType = mappableEntity.FieldUniqueId;
    dataField.RecordPropertyName = mappableEntity.FieldId;
    dataField.ListType = mappableEntity.ListType;
    dataField.ListKey = mappableEntity.ListKey;
    dataField.Path = mappableEntity.Path;
    dataField.ModelPropertyName = mappableEntity.ModelPropertyName;
    dataField.LinkedRecordModuleId = mappableEntity.LinkedRecordModuleId;

    this.setListAttributes(dataField);
    }

  attributeMapsToChanged(selection: string, dataFieldIndex: number, attributeIndex: number): void {
    if (!selection) {
      return;
    }

    const attribute = this.model.DataFields[dataFieldIndex]?.AttributeMappings[attributeIndex];
    if (!attribute) {
      return;
    }

    const mappableEntity = this.findMappableEntityByKey(selection);
    if (!mappableEntity) {
      return;
    }

    attribute.DataType = mappableEntity.FieldUniqueId;
    attribute.RecordPropertyName = mappableEntity.FieldId;
    attribute.ListType = mappableEntity.ListType;
    attribute.ListKey = mappableEntity.ListKey;
    attribute.Path = mappableEntity.Path;
    attribute.ModelPropertyName = mappableEntity.ModelPropertyName;
    attribute.LinkedRecordModuleId = mappableEntity.LinkedRecordModuleId;
    }

  autoMapButtonDisabled(): boolean {
    if (this.isModuleRecordFeed()){
        return !this.mappableEntities || this.mappableEntities.length <= 0 || this.model.DataFields.length <= 0;
    }
    return this.model.DataFields.length == 0;
  }
  
  autoMapFields(): void {
      const parsedFeedType = FeedTypes[this.model.FeedType];
      switch(parsedFeedType){
          case FeedTypes.ModuleRecordFeed:
              this.feedAutoMapperService.autoMapModuleRecordFields(
                  this.model.DataFields,
                  this.mappableEntities,
                  this.overwritePreviousMappings);
              break;
          case FeedTypes.HierarchyFeed:
              this.feedAutoMapperService.autoMapHierarchyFields(
                  this.model.DataFields,
                  this.hierarchyAttributes,
                  this.overwritePreviousMappings,
                  this.selectedAttributes);
              break;
      }
  }

    externalIdChanged(selection: string, dataFieldIndex: number): void {
        if (!selection) {
            return;
        }

    const dataField = this.model.DataFields[dataFieldIndex];
    dataField.LinkedRecordExternalIdProperty = selection;
  }

  attributeExternalIdChanged(selection: string, dataFieldIndex: number, attributeIndex: number): void {
    if (!selection) {
      return;
    }

    const attributeMapping = this.model.DataFields[dataFieldIndex].AttributeMappings[attributeIndex];
    if (attributeMapping) {
      attributeMapping.LinkedRecordExternalIdProperty = selection;
    }
  }

    moduleChanged(): void {
        if (this.model.ModuleId) {
            this.importService
                .getImportStates(this.tenant.TenantId, this.model.ModuleId)
                .pipe(takeUntil(this.isDestroyed$))
                .subscribe((result) => {
                    this.states = result;
                });
        } else {
            this.states = [];
        }
    }

    onHierarchyAttributeLocationChanged(selection: LocationAttribute, dataFieldIndex: number): void {
        if (!selection) {
            return;
        }

        this.model.DataFields[dataFieldIndex].AttributeType = selection.AttributeType;
        this.model.DataFields[dataFieldIndex].RegexRestrict = selection.RegexRestrict;
        this.model.DataFields[dataFieldIndex].MaxChars = selection.MaxChars;
        this.model.DataFields[dataFieldIndex].AttributeId = selection.AttributeId;
        this.model.DataFields[dataFieldIndex].MinChars = selection.MinChars;
        this.model.DataFields[dataFieldIndex].ListKey = selection.ListAlias;
        this.model.DataFields[dataFieldIndex].ListType = selection.ListType;
    }

    stateChanged(): void {
        const latestModuleVersion = this.moduleVersions[this.model.ModuleId];

        this.feedService
            .getMappableEntities(
                this.tenant,
                this.model.ModuleId,
                latestModuleVersion,
                this.model.StateId
            )
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((entities) => {
                this.mappableEntities = entities;
                entities.forEach((entity) => this.getLinkedEntities(entity));
            });
    }

    getLinkedRecordOptions(moduleId: string): string[] {
        return this.linkedRecordOptions.get(moduleId);
    }

    ngOnDestroy(): void {
        this.isDestroyed$.next();
    }
    fieldDisabled(field: IFeedConfigurationDataField): void{
        if(!this.isListOrAttributeFilter(field)){
            return;
        }

        field.AttributeMappings.forEach(atttributeMapping => {
            atttributeMapping.Enabled = field.Enabled;
        });
    }

  private isListOrAttributeFilter(field: IFeedConfigurationDataField){
    return field.ListType === 'AIRSWEBLIST' || field.ListType === 'AttributeFilter';
  }

  // Entities in different multis can share a FieldId, ModelPropertyName and ListKey, so the path
  // has to be part of the key for the dropdown to tell them apart.
  private buildMapsToKey(fieldId: string, modelPropertyName: string, listKey: string, path: string[]): string {
    return [fieldId, modelPropertyName, listKey, (path ?? []).join('.')].join('¦');
  }

  private findMappableEntityByKey(key: string): ModuleRecordFeedMappableEntityDto {
    return this.mappableEntities?.find((entity) => this.mappableEntityKey(entity) === key);
  }

  private setListAttributes(field: IFeedConfigurationDataField): void {
      field.AttributeMappings = [];
     if (!this.isListOrAttributeFilter(field)) {
      return;
    }

    this.tenantService
                .getListAttributeDefinitions(field.ListKey)
                .pipe(takeUntil(this.isDestroyed$))
                .subscribe(res => {
                    field.AttributeMappings = res.map(att => this.createAttributeMapping(att.Name));
                });
  }

  private createAttributeMapping(name: string): AttributeMapping {
    return {
      AttributeName: name, 
      DataType: 'string',
      ListKey: '',
      ListType: '',
      RecordPropertyName: '', 
      Enabled: true,
      ModelPropertyName: '', 
      Path: [], 
      LinkedRecordExternalIdProperty: '',
      LinkedRecordModuleId: '',
      IsNaturalKey: false
    };
  }

  
    private setSelectedAttributes(): void {
        this.model.DataFields.forEach((field) => {
            let locationAttribute = this.hierarchyAttributes.find(attr => attr?.AttributeId === field?.AttributeId);
            locationAttribute = locationAttribute ? locationAttribute : new LocationAttribute();
            this.selectedAttributes.push(locationAttribute);
        });
    }

    private buildDateFormatStrings(): void {
        const delim = ['-', '/', '.'];
        const dates = ['yyyy-MM-dd', 'dd-MM-yyyy', 'MM-dd-yyyy'];
        const time = ['', 'HH:mm:ss', 'HH:mm'];

        delim.forEach((delimItem) => {
            dates.forEach((date) => {
                time.forEach((timeItem) => {
                    const formatString =
                        date.replace(new RegExp('-', 'g'), delimItem) +
                        (timeItem.length === 0 ? '' : ' ' + timeItem);
                    this.formatStrings.push({
                        FriendlyName: formatString,
                        Key: formatString,
                    });
                });
            });
        });
    }

    private subscribeToLists(): void {
        this.tenantService
            .getLists()
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((result) => result.forEach((list) => this.lists.push(list)));
    }

    private subscribeToFeedConfiguration(): void {
        this.configId = this.getRouteConfigId();
        combineLatest([
            this.moduleService.getCurrentModuleVersions(this.tenantService.selectedTenant),
            this.feedService.getFeedConfiguration(
                this.tenantService.selectedTenant.Value,
                this.configId
            ),
            this.locationAttributeService.getHierarchyAttributes(
                this.tenantService.selectedTenant
            ),
        ])
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(([moduleVersions, result, hierarchyAttributes]) => {
                this.moduleVersions = moduleVersions;
                this.model = result;
                this.isLoading = false;
                this.getListOfDataTypes();
                this.setListOfFeedTypes();
                this.setOshaEstablishmentTypes();
                this.checkFeedNameUnique();
                this.checkFolderFilePatternUnique();
                this.checkIdentifierCount();
                this.calculateSyncColumn();

                if (this.configId === this.emptyGuid) {
                    this.model.FeedType = FeedTypes[FeedTypes.LookupListFeed];
                }

                this.initialiseModuleRecordFeed();

                this.hierarchyAttributes = hierarchyAttributes;
                this.setSelectedAttributes();
            });
    }

    private setLocationLevels(): void {
        this.feedService
            .getLocationLevels(this.tenantService.selectedTenant.Value)
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((result) => {
                let i = 1;
                while (i <= 6) {
                    const translation = result.filter(
                        (a) => a.LevelAlias.toLocaleLowerCase() === `level${i}`
                    )[0];

                    const locationLevel: ILocationLevel = {
                        LevelAlias: `LEVEL${i}`,
                        LevelName:
                            translation !== undefined ? translation.LevelName : `Level ${i}`,
                    };

                    this.locationLevels.push(locationLevel);
                    i++;
                }

                const reportingTranslation = result.filter(
                    (a) => a.LevelAlias.toLocaleLowerCase() === 'reporting_point'
                )[0];
                const reportingPoint: ILocationLevel = {
                    LevelAlias: 'REPORTING_POINT',
                    LevelName:
                        reportingTranslation !== undefined
                            ? reportingTranslation.LevelName
                            : 'REPORTING_POINT',
                };

                this.locationLevels.push(reportingPoint);
            });
    }

    private handleSaveFeedConfigurationError(): void {
        this.saveFeedConfigurationErrorText = this.msgErrorSave;
    }

    private getLinkedEntities(entity: ModuleRecordFeedMappableEntityDto): void {
        if (!entity.LinkedRecordModuleId ||
            entity.LinkedRecordModuleId.length <= 0 ||
            this.linkedRecordOptions.get(entity.LinkedRecordModuleId)) {
            return;
        }

        this.linkedRecordOptions.set(entity.LinkedRecordModuleId, []);

        const latestLinkedModuleVersion = this.moduleVersions[entity.LinkedRecordModuleId];
        this.feedService
            .getMappableEntities(
                this.tenant,
                entity.LinkedRecordModuleId,
                latestLinkedModuleVersion,
                0
            )
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((linkedEntities) => {
                this.linkedRecordOptions.set(
                    entity.LinkedRecordModuleId,
                    linkedEntities.map((e) => e.ModelPropertyName)
                );
            });
    }

    private populateFieldListFromFileContents(fileContents: any[]): void {
    const previousDataFieldsWithMappings = this.model.DataFields;
        this.model.DataFields = [];

    Object.keys(fileContents[0]).forEach((k, i) =>
      this.model.DataFields.push({
        Name: k,
        Index: i,
        IsSearchKey: false,
        IsNaturalKey: false,
        DataType: this.isHierarchyFeed() ? 'hierarchy' : 'string',
        ListKey: null,
        IsIdentifier: false,
        FormatString: null,
        SyncList: false,
        DisableSync: true,
        LocationLevel: null,
        RecordPropertyName: null,
        Enabled: true,
        Path: [],
        ModelPropertyName: null,
        LinkedRecordExternalIdProperty: null,
        LinkedRecordModuleId: null,
        ListType: null,
        AttributeType: null,
        RegexRestrict: null,
        MaxChars: null,
        MinChars: null,
        AttributeId: null,
        AttributeMappings: []
      })
    );

    if (!previousDataFieldsWithMappings) {
      return;
    }

    this.reMapPreviousMappings(previousDataFieldsWithMappings);
  }

  private reMapPreviousMappings(previousDataFieldsWithMappings: IFeedConfigurationDataField[]) {
    for (const dataField of this.model.DataFields) {
      const previousDataFieldWithMapping = previousDataFieldsWithMappings
        .find((previousDataField) => previousDataField.Name === dataField.Name);

      if (!previousDataFieldWithMapping) {
        continue;
      }

      dataField.IsSearchKey = previousDataFieldWithMapping.IsSearchKey;
      dataField.IsNaturalKey = previousDataFieldWithMapping.IsNaturalKey;
      dataField.DataType = previousDataFieldWithMapping.DataType;
      dataField.ListKey = previousDataFieldWithMapping.ListKey;
      dataField.IsIdentifier = previousDataFieldWithMapping.IsIdentifier;
      dataField.FormatString = previousDataFieldWithMapping.FormatString;
      dataField.SyncList = previousDataFieldWithMapping.SyncList;
      dataField.DisableSync = previousDataFieldWithMapping.DisableSync;
      dataField.LocationLevel = previousDataFieldWithMapping.LocationLevel;
      dataField.RecordPropertyName = previousDataFieldWithMapping.RecordPropertyName;
      dataField.Enabled = previousDataFieldWithMapping.Enabled;
      dataField.Path = previousDataFieldWithMapping.Path;
      dataField.ModelPropertyName = previousDataFieldWithMapping.ModelPropertyName;
      dataField.LinkedRecordExternalIdProperty = previousDataFieldWithMapping.LinkedRecordExternalIdProperty;
      dataField.LinkedRecordModuleId = previousDataFieldWithMapping.LinkedRecordModuleId;
      dataField.ListType = previousDataFieldWithMapping.ListType;
      dataField.AttributeType = previousDataFieldWithMapping.AttributeType;
      dataField.RegexRestrict = previousDataFieldWithMapping.RegexRestrict;
      dataField.MaxChars = previousDataFieldWithMapping.MaxChars;
      dataField.MinChars = previousDataFieldWithMapping.MinChars;
      dataField.AttributeId = previousDataFieldWithMapping.AttributeId;
    }
  }

    private setIsModuleEnabled(): void {
        const isNewModuleRecordFeed =
            !this.model.ModuleId && this.recordFeedModules.length > 0;

        const selectedModuleIsEnabled =
            this.isModuleRecordFeed() &&
            this.model.ModuleId &&
            this.recordFeedModules &&
            this.recordFeedModules.some(
                (module) => module.ModuleId === this.model.ModuleId
            );

        this.moduleIsEnabled = isNewModuleRecordFeed || selectedModuleIsEnabled;
    }

    private handleFetchFileError(error: any): void {
        switch (error.status) {
            case 400:
                this.fetchFileErrorText = `${error.error.Message}`;
                break;
            case 404:
                this.fetchFileErrorText = this.msgErrorNoFile;
                break;
            case 500:
                this.fetchFileErrorText = this.msgErrorServer;
                break;
            default:
                this.fetchFileErrorText = this.msgErrorOther;
                break;
        }
    }

    private showSaveConfirmationMessage(): void {
        const config = new MatSnackBarConfig();
        config.duration = 10000;
        this.snackBar.open(this.msgConfigSaved, undefined, config);
    }

    private getIdentifierRowsCount(): number {
        return this.model.DataFields.filter((f) => f.IsIdentifier).length;
    }

    private getListOfDataTypes(): void {
        if (this.isHierarchyFeed()) {
            this.dataTypes = [
                {
                    FriendlyName: 'Hierarchy',
                    Key: 'hierarchy',
                },
                {
                    FriendlyName: 'Hierarchy Attributes',
                    Key: 'attributes',
                },
            ];
        } else if (this.isOshaEstablishmentFeed()) {
            this.dataTypes = [
                {
                    FriendlyName: 'Text',
                    Key: 'string',
                },
            ];
        } else if (this.isModuleRecordFeed()) {
            this.dataTypes = [
                {
                    FriendlyName: 'Text',
                    Key: 'string',
                },
                {
                    FriendlyName: 'List',
                    Key: 'list',
                },
                {
                    FriendlyName: 'Date',
                    Key: 'date',
                },
            ];
        } else {
            this.dataTypes = [
                {
                    FriendlyName: 'Text',
                    Key: 'string',
                },
                {
                    FriendlyName: 'List',
                    Key: 'list',
                },
                {
                    FriendlyName: 'Date',
                    Key: 'date',
                },
                {
                    FriendlyName: 'Hierarchy',
                    Key: 'hierarchy',
                },
            ];
        }
    }

    private setListOfFeedTypes(): void {
        const items = Object.keys(FeedTypes).filter((item) => isNaN(Number(item)));

        items.forEach((_, index) => {
            this.feedTypes.push({
                FriendlyName: FeedTypeDescription.get(+index),
                Key: FeedTypes[index],
            });
        });
    }

    private setOshaEstablishmentTypes(): void {
        this.oshaEstablishmentTypes = [];
        if (this.isHierarchyOshaAttributesFeed()) {
            this.oshaEstablishmentTypes.push({
                FriendlyName: 'Hierarchy ShortName',
                Key: 'ReportingPointShortName',
            });
        }
        this.oshaEstablishmentTypes.push(
            {
                FriendlyName: 'ID',
                Key: 'EstablishmentId',
            },
            {
                FriendlyName: 'City',
                Key: 'City',
            },
            {
                FriendlyName: 'Company Name',
                Key: 'CompanyName',
            },
            {
                FriendlyName: 'EIN',
                Key: 'Ein',
            },
            {
                FriendlyName: 'Establishment Name',
                Key: 'EstablishmentName',
            },
            {
                FriendlyName: 'Establishment Type',
                Key: 'EstablishmentType',
            },
            {
                FriendlyName: 'Establishment Status',
                Key: 'EstablishmentStatus',
            },
            {
                FriendlyName: 'Executive Name',
                Key: 'ExecutiveName',
            },
            {
                FriendlyName: 'Executive Phone',
                Key: 'ExecutivePhone',
            },
            {
                FriendlyName: 'Executive Title',
                Key: 'ExecutiveTitle',
            },
            {
                FriendlyName: 'Included in Batch',
                Key: 'IsIncludedInBatch',
            },
            {
                FriendlyName: 'Industry Description',
                Key: 'IndustryDescription',
            },
            {
                FriendlyName: 'NAICS Code',
                Key: 'NaicsCode',
            },
            {
                FriendlyName: 'SIC Code',
                Key: 'SicCode',
            },
            {
                FriendlyName: 'Size',
                Key: 'Size',
            },
            {
                FriendlyName: 'State',
                Key: 'State',
            },
            {
                FriendlyName: 'Street Address',
                Key: 'StreetAddress',
            },
            {
                FriendlyName: 'Year',
                Key: 'YearValidFrom',
            },
            {
                FriendlyName: 'Zip',
                Key: 'Zip',
            }
        );
        if (this.isOshaEstablishmentFeed()) {
            this.oshaEstablishmentTypes.push(
                {
                    FriendlyName: 'Annual Average Employees',
                    Key: 'AnnualAverageEmployees',
                },
                {
                    FriendlyName: 'Total Hours Worked',
                    Key: 'TotalHoursWorked',
                }
            );
        }
    }

}